/* FREDDY Pantroom — bag drawer + cart page (00-architecture §E.3).
   Cart AJAX API with bundled Section Rendering: every request asks Shopify to re-render the bag
   section (#bag-content), the header section (#bag-count) and the cart-page section(s) in the
   same response. Section ids are dynamic (sections--…__bag, template--…__main) and are always
   read from the DOM. */
(() => {
  'use strict';

  const F = (window.FREDDY = window.FREDDY || {});
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const routes = F.routes || {};
  const ROUTE_ADD = routes.cartAdd || root + 'cart/add';
  const ROUTE_CHANGE = routes.cartChange || root + 'cart/change';
  const MAX_SECTIONS = 5;
  const DEBOUNCE_MS = 300;

  const strings = () => F.strings || {};
  const asElement = (target) => (target instanceof Element ? target : target && target.parentElement) || null;
  /* Liquid `t` output is HTML-escaped on Shopify (keys without _html): decode before using it as text. */
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    const raw = String(value == null ? '' : value);
    if (raw.indexOf('&') === -1) return raw;
    decoder.innerHTML = raw;
    return decoder.value;
  };

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function wrapperId(el) {
    if (typeof F.wrapperId === 'function') return F.wrapperId(el);
    const wrapper = el && el.closest ? el.closest('.shopify-section') : null;
    return wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : null;
  }

  function bagSectionId() {
    const dialog = document.getElementById('bag-dialog');
    return dialog ? wrapperId(dialog) : null;
  }

  function headerSectionId() {
    const count = document.getElementById('bag-count');
    return count ? wrapperId(count.closest('header') || count) : null;
  }

  function sectionIds() {
    const ids = [];
    const push = (id) => {
      if (id && !ids.includes(id) && ids.length < MAX_SECTIONS) ids.push(id);
    };
    push(bagSectionId());
    push(headerSectionId());
    document.querySelectorAll('[data-freddy-cart-section]').forEach((el) => push(el.getAttribute('data-freddy-cart-section')));
    return ids;
  }

  function withSections(body) {
    const ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = location.pathname;
    }
    return body;
  }

  async function post(url, body, source) {
    let response;
    let json = null;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      });
      json = await response.json().catch(() => null);
    } catch (networkError) {
      response = null;
    }
    if (!response || !response.ok) {
      const description = json && typeof json.description === 'string' ? json.description : '';
      const message = description || (json && typeof json.message === 'string' ? json.message : '') || decode(strings().cartError) || 'Error';
      const status = response ? response.status : 0;
      const error = new Error(message);
      error.status = status;
      dispatch('freddy:cart:error', { message, status, source });
      throw error;
    }
    return json || {};
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function clearDrawerError() {
    document.querySelectorAll('.freddy-bag-error').forEach((el) => el.remove());
  }

  function render(sections) {
    if (!sections || typeof sections !== 'object') return;
    const bagId = bagSectionId();
    const headerId = headerSectionId();
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (typeof html !== 'string') return;
      const doc = parse(html);

      if (id === bagId) {
        const fresh = doc.getElementById('bag-content');
        const current = document.getElementById('bag-content');
        if (fresh && current) current.replaceWith(document.importNode(fresh, true));
      }

      if (id === headerId) {
        const fresh = doc.getElementById('bag-count');
        const current = document.getElementById('bag-count');
        if (fresh && current) current.textContent = fresh.textContent;
      }

      const selector = '[data-freddy-cart-section="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]';
      const freshPage = doc.querySelector(selector);
      if (freshPage) document.querySelectorAll(selector).forEach((el) => { el.innerHTML = freshPage.innerHTML; });
    });
  }

  function shouldOpenDrawer() {
    if (!document.getElementById('bag-dialog')) return false;
    const config = document.getElementById('freddy-bag-config');
    if (!config) return true;
    try {
      return JSON.parse(config.textContent).open_on_add !== false;
    } catch (error) {
      return true;
    }
  }

  function openDrawer() {
    if (typeof F.show === 'function') {
      F.show('#bag-dialog');
      return;
    }
    const dialog = document.getElementById('bag-dialog');
    if (dialog && !dialog.open) dialog.showModal();
  }

  async function add(items, options = {}) {
    const source = options.source || '';
    const json = await post(ROUTE_ADD + '.js', withSections({ items }), source);
    clearDrawerError();
    render(json.sections);
    dispatch('freddy:cart:updated', { source, items: json.items });
    if (shouldOpenDrawer()) openDrawer();
    return json;
  }

  /* `line` = 1-based line number, or a line-item key string ("variant:hash", Cart API `id`). */
  async function change(line, quantity, options = {}) {
    const source = options.source || '';
    const target = typeof line === 'string' && line.indexOf(':') !== -1 ? { id: line } : { line: Number(line) };
    const json = await post(ROUTE_CHANGE + '.js', withSections(Object.assign(target, { quantity: Number(quantity) })), source);
    clearDrawerError();
    render(json.sections);
    dispatch('freddy:cart:updated', { source, item_count: json.item_count });
    return json;
  }

  async function refresh() {
    const ids = sectionIds();
    if (!ids.length) return null;
    try {
      const response = await fetch(location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { credentials: 'same-origin' });
      if (!response.ok) return null;
      const json = await response.json();
      render(json);
      return json;
    } catch (error) {
      return null;
    }
  }

  /* ---------- drawer: remove buttons ---------- */

  function showDrawerError(message) {
    clearDrawerError();
    const head = document.querySelector('#bag-dialog .dialog-head');
    if (!head) return;
    const p = document.createElement('p');
    p.className = 'error freddy-bag-error';
    p.setAttribute('role', 'alert');
    p.textContent = message;
    head.after(p);
  }

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    const button = target && target.closest('#bag-content [data-remove]');
    if (!button) return;
    event.preventDefault();
    const content = document.getElementById('bag-content');
    if (content && content.getAttribute('aria-busy') === 'true') return;
    const line = Number(button.getAttribute('data-remove')) + 1;
    if (content) content.setAttribute('aria-busy', 'true');
    change(line, 0, { source: 'drawer' })
      .catch((error) => showDrawerError(error.message))
      .finally(() => {
        const current = document.getElementById('bag-content');
        if (current) current.removeAttribute('aria-busy');
      });
  });

  /* ---------- cart page (P10 markup: [data-freddy-cart-section], input[name="updates[]"][data-line][data-key], [data-cart-remove][data-key]) ---------- */

  const timers = new Map();
  let queue = Promise.resolve();

  /* Line-item key when the markup provides one (stable while other lines are removed), else the line number. */
  const lineRef = (el) => el.getAttribute('data-key') || Number(el.getAttribute('data-line') || el.getAttribute('data-cart-remove'));

  function cartPageError(section, message) {
    if (!section) return;
    section.querySelectorAll('.freddy-bag-error').forEach((el) => el.remove());
    const p = document.createElement('p');
    p.className = 'error freddy-bag-error';
    p.setAttribute('role', 'alert');
    p.textContent = message;
    section.prepend(p);
  }

  /* Requests run one after another: each response re-renders the lines the next one relies on. */
  function cartPageChange(section, line, quantity) {
    if (section) section.setAttribute('aria-busy', 'true');
    const run = () => change(line, quantity, { source: 'cart-page' })
      .catch((error) => refresh().then(() => cartPageError(document.querySelector('[data-freddy-cart-section]'), error.message)))
      .finally(() => {
        document.querySelectorAll('[data-freddy-cart-section]').forEach((el) => el.removeAttribute('aria-busy'));
      });
    queue = queue.then(run, run);
    return queue;
  }

  function scheduleQuantity(input, delay) {
    const line = lineRef(input);
    if (!line) return;
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    const section = input.closest('[data-freddy-cart-section]');
    clearTimeout(timers.get(line));
    timers.set(line, setTimeout(() => {
      timers.delete(line);
      cartPageChange(section, line, quantity);
    }, delay));
  }

  const isQuantityInput = (el) => el && el.matches && el.matches('[data-freddy-cart-section] input[name="updates[]"]');

  document.addEventListener('change', (event) => {
    const input = asElement(event.target);
    if (isQuantityInput(input)) scheduleQuantity(input, DEBOUNCE_MS);
  });

  /* Enter in a quantity field would submit the form with its first submit button (= checkout):
     commit the value instead (blur fires the native change event, focus returns to the field). */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = asElement(event.target);
    if (!isQuantityInput(input)) return;
    event.preventDefault();
    input.blur();
    input.focus();
  });

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    const button = target && target.closest('[data-freddy-cart-section] [data-cart-remove]');
    if (!button) return;
    event.preventDefault();
    const line = lineRef(button);
    if (!line) return;
    /* a quantity edit of this line that is still debounced (change fired on blur) is superseded */
    clearTimeout(timers.get(line));
    timers.delete(line);
    cartPageChange(button.closest('[data-freddy-cart-section]'), line, 0);
  });

  /* ---------- back/forward cache: the cart may have changed in another tab ---------- */

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  F.cart = Object.assign(F.cart || {}, { sectionIds, add, change, render, refresh });
})();
